/**
 * TakeGrid — "Take" Responsibility Grid
 *
 * Bridges the "Air Sandwich" between executive strategy and ground-level execution.
 * Each card represents one org unit's commitment:
 *   - Takes (committed actions, max 5)
 *   - Interdependencies (what we need from other teams)
 *   - Deliverables (what we produce)
 *   - Milestone date
 */

import React, { useState, useCallback } from 'react';
import {
  PlusIcon,
  TrashIcon,
  BuildingOfficeIcon,
  ArrowsRightLeftIcon,
  ClipboardDocumentListIcon,
  BoltIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useStrategyKernelStore } from '../../../stores/prioritization/strategyKernelStore';
import type {
  StrategyTake,
  TakeAction,
  TakeInterdependency,
  TakeDeliverable,
  ProximateObjective,
} from '../../../types/prioritization/strategyKernel';
import { MAX_TAKE_ACTIONS } from '../../../types/prioritization/strategyKernel';

// ============================================================================
// Inline row editors
// ============================================================================

interface ActionRowProps {
  action: TakeAction;
  onUpdate: (id: string, updates: Partial<TakeAction>) => void;
  onRemove: (id: string) => void;
}

function ActionRow({ action, onUpdate, onRemove }: ActionRowProps) {
  return (
    <div className="flex items-center gap-2 group py-1">
      <div className="flex-1 flex items-center gap-2">
        <input
          type="text"
          value={action.title}
          onChange={e => onUpdate(action.id, { title: e.target.value })}
          placeholder="Action"
          className="flex-1 text-sm border-b border-[var(--rule)] focus:border-[var(--slate)] outline-none bg-transparent text-[var(--text-ter)] placeholder-neutral-600"
        />
        <input
          type="date"
          value={action.dueDate ?? ''}
          onChange={e => onUpdate(action.id, { dueDate: e.target.value })}
          className="text-xs text-[var(--text-ter)] border border-[var(--rule)] rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-blue-500 w-32 bg-[var(--bg-raised)]"
        />
      </div>
      <button
        onClick={() => onRemove(action.id)}
        className="shrink-0 text-[var(--text-sec)] hover:text-[var(--coral)] opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <TrashIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

interface DepRowProps {
  dep: TakeInterdependency;
  onUpdate: (id: string, updates: Partial<TakeInterdependency>) => void;
  onRemove: (id: string) => void;
}

function DepRow({ dep, onUpdate, onRemove }: DepRowProps) {
  return (
    <div className="flex items-center gap-2 group py-1">
      <input
        type="text"
        value={dep.fromUnitName ?? ''}
        onChange={e => onUpdate(dep.id, { fromUnitName: e.target.value })}
        placeholder="From team"
        className="w-28 text-xs text-[var(--text-ter)] border-b border-[var(--rule)] focus:border-[var(--slate)] outline-none bg-transparent shrink-0 placeholder-neutral-600"
      />
      <input
        type="text"
        value={dep.deliverable}
        onChange={e => onUpdate(dep.id, { deliverable: e.target.value })}
        placeholder="Deliverable needed"
        className="flex-1 text-sm border-b border-[var(--rule)] focus:border-[var(--slate)] outline-none bg-transparent text-[var(--text-ter)] placeholder-neutral-600"
      />
      <input
        type="date"
        value={dep.dueDate ?? ''}
        onChange={e => onUpdate(dep.id, { dueDate: e.target.value })}
        className="text-xs text-[var(--text-ter)] border border-[var(--rule)] rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-purple-500 w-32 bg-[var(--bg-raised)]"
      />
      <button
        onClick={() => onRemove(dep.id)}
        className="shrink-0 text-[var(--text-sec)] hover:text-[var(--coral)] opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <TrashIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

interface DelRowProps {
  del: TakeDeliverable;
  onUpdate: (id: string, updates: Partial<TakeDeliverable>) => void;
  onRemove: (id: string) => void;
}

function DelRow({ del, onUpdate, onRemove }: DelRowProps) {
  return (
    <div className="flex items-center gap-2 group py-1">
      <input
        type="text"
        value={del.title}
        onChange={e => onUpdate(del.id, { title: e.target.value })}
        placeholder="Deliverable"
        className="flex-1 text-sm border-b border-[var(--rule)] focus:border-[var(--green)] outline-none bg-transparent text-[var(--text-ter)] placeholder-neutral-600"
      />
      <input
        type="date"
        value={del.dueDate ?? ''}
        onChange={e => onUpdate(del.id, { dueDate: e.target.value })}
        className="text-xs text-[var(--text-ter)] border border-[var(--rule)] rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-green-500 w-32 bg-[var(--bg-raised)]"
      />
      <button
        onClick={() => onRemove(del.id)}
        className="shrink-0 text-[var(--text-sec)] hover:text-[var(--coral)] opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <TrashIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ============================================================================
// Section header
// ============================================================================

function SectionHeader({ icon, label, count, maxCount }: { icon: React.ReactNode; label: string; count: number; maxCount?: number }) {
  return (
    <div className="flex items-center gap-2 text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">
      {icon}
      <span>{label}</span>
      {maxCount !== undefined && (
        <span className={`ml-auto font-normal normal-case tracking-normal ${count >= maxCount ? 'text-[var(--mustard)]' : 'text-[var(--text-sec)]'}`}>
          {count}/{maxCount}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// Single Take Card
// ============================================================================

interface TakeCardProps {
  take: StrategyTake;
  objective?: ProximateObjective;
}

function TakeCard({ take, objective }: TakeCardProps) {
  const { updateTake, deleteTake } = useStrategyKernelStore();
  const [orgUnitName, setOrgUnitName] = useState(take.orgUnitName ?? '');

  const atActionLimit = take.committedActions.length >= MAX_TAKE_ACTIONS;

  const saveOrgUnit = () => {
    updateTake(take.id, { orgUnitId: take.orgUnitId });
  };

  const addAction = useCallback(() => {
    if (take.committedActions.length >= MAX_TAKE_ACTIONS) return;
    const newAction: TakeAction = { id: crypto.randomUUID(), title: '', dueDate: undefined };
    updateTake(take.id, { committedActions: [...take.committedActions, newAction] });
  }, [take, updateTake]);

  const updateAction = useCallback((id: string, updates: Partial<TakeAction>) => {
    const actions = take.committedActions.map(a => a.id === id ? { ...a, ...updates } : a);
    updateTake(take.id, { committedActions: actions });
  }, [take, updateTake]);

  const removeAction = useCallback((id: string) => {
    updateTake(take.id, { committedActions: take.committedActions.filter(a => a.id !== id) });
  }, [take, updateTake]);

  const addDep = useCallback(() => {
    const newDep: TakeInterdependency = { id: crypto.randomUUID(), deliverable: '', fromUnitName: '' };
    updateTake(take.id, { interdependencies: [...take.interdependencies, newDep] });
  }, [take, updateTake]);

  const updateDep = useCallback((id: string, updates: Partial<TakeInterdependency>) => {
    const deps = take.interdependencies.map(d => d.id === id ? { ...d, ...updates } : d);
    updateTake(take.id, { interdependencies: deps });
  }, [take, updateTake]);

  const removeDep = useCallback((id: string) => {
    updateTake(take.id, { interdependencies: take.interdependencies.filter(d => d.id !== id) });
  }, [take, updateTake]);

  const addDel = useCallback(() => {
    const newDel: TakeDeliverable = { id: crypto.randomUUID(), title: '' };
    updateTake(take.id, { deliverables: [...take.deliverables, newDel] });
  }, [take, updateTake]);

  const updateDel = useCallback((id: string, updates: Partial<TakeDeliverable>) => {
    const dels = take.deliverables.map(d => d.id === id ? { ...d, ...updates } : d);
    updateTake(take.id, { deliverables: dels });
  }, [take, updateTake]);

  const removeDel = useCallback((id: string) => {
    updateTake(take.id, { deliverables: take.deliverables.filter(d => d.id !== id) });
  }, [take, updateTake]);

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
      {/* Card header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-neutral-750 border-b border-[var(--rule)]" style={{ background: 'rgb(30 32 38)' }}>
        <div className="flex items-center gap-2 flex-1">
          <BuildingOfficeIcon className="w-4 h-4 text-[var(--text-ter)] shrink-0" />
          <input
            type="text"
            value={orgUnitName}
            onChange={e => setOrgUnitName(e.target.value)}
            onBlur={saveOrgUnit}
            placeholder="Team / Division"
            className="text-sm font-semibold text-[var(--text-ter)] bg-transparent border-none outline-none placeholder-neutral-600"
          />
        </div>
        {objective && (
          <span className="text-xs text-[var(--text-ter)] bg-[var(--bg-raised)] rounded px-2 py-0.5 truncate max-w-36">
            {objective.title}
          </span>
        )}
        <div className="flex items-center gap-1">
          <label className="text-xs text-[var(--text-ter)]">Milestone:</label>
          <input
            type="date"
            value={take.milestoneDate ?? ''}
            onChange={e => updateTake(take.id, { milestoneDate: e.target.value || undefined })}
            className="text-xs text-[var(--text-ter)] border border-[var(--rule)] rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-blue-500 bg-[var(--bg-raised)]"
          />
        </div>
        <button
          onClick={() => deleteTake(take.id)}
          className="text-[var(--text-sec)] hover:text-[var(--coral)] transition-colors"
          title="Delete take"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[var(--rule)]">
        {/* ── Takes (committed actions) ── */}
        <div className="p-4">
          <SectionHeader
            icon={<BoltIcon className="w-3.5 h-3.5 text-[var(--text-ter)]" />}
            label="Takes"
            count={take.committedActions.length}
            maxCount={MAX_TAKE_ACTIONS}
          />
          {take.committedActions.map(a => (
            <ActionRow key={a.id} action={a} onUpdate={updateAction} onRemove={removeAction} />
          ))}
          {atActionLimit && (
            <div className="flex items-center gap-1 text-xs text-[var(--mustard)] mt-1">
              <ExclamationTriangleIcon className="w-3.5 h-3.5 shrink-0" />
              Limit reached — focus on these {MAX_TAKE_ACTIONS}
            </div>
          )}
          {!atActionLimit && (
            <button onClick={addAction} className="mt-1 flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)]">
              <PlusIcon className="w-3.5 h-3.5" /> Add action
            </button>
          )}
        </div>

        {/* ── Interdependencies ── */}
        <div className="p-4">
          <SectionHeader
            icon={<ArrowsRightLeftIcon className="w-3.5 h-3.5 text-[var(--text-ter)]" />}
            label="Interdependencies"
            count={0}
          />
          {take.interdependencies.map(d => (
            <DepRow key={d.id} dep={d} onUpdate={updateDep} onRemove={removeDep} />
          ))}
          <button onClick={addDep} className="mt-1 flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)]">
            <PlusIcon className="w-3.5 h-3.5" /> Add dependency
          </button>
        </div>

        {/* ── Deliverables ── */}
        <div className="p-4">
          <SectionHeader
            icon={<ClipboardDocumentListIcon className="w-3.5 h-3.5 text-[var(--green)]" />}
            label="Deliverables"
            count={0}
          />
          {take.deliverables.map(d => (
            <DelRow key={d.id} del={d} onUpdate={updateDel} onRemove={removeDel} />
          ))}
          <button onClick={addDel} className="mt-1 flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]">
            <PlusIcon className="w-3.5 h-3.5" /> Add deliverable
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Top-level TakeGrid
// ============================================================================

interface TakeGridProps {
  kernelId: string;
}

export function TakeGrid({ kernelId }: TakeGridProps) {
  const { getTakesForKernel, getObjectivesForKernel, createTake } = useStrategyKernelStore();

  const takes = getTakesForKernel(kernelId);
  const objectives = getObjectivesForKernel(kernelId);
  const objMap = Object.fromEntries(objectives.map(o => [o.id, o]));

  const handleAddTake = useCallback(async () => {
    await createTake({ kernelId });
  }, [kernelId, createTake]);

  return (
    <div className="space-y-4">
      {/* Instructions */}
      <div className="text-sm text-[var(--text-ter)] bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3">
        <p className="font-medium text-[var(--text-ter)] mb-1">Take Responsibility Grid</p>
        <p>Capture explicit commitments from each team. This closes the "air sandwich" between executive strategy and ground-level execution. Every team names their Takes, their Interdependencies (what they need from others), and their Deliverables — with dates.</p>
      </div>

      {/* Cards */}
      {takes.map(take => (
        <TakeCard
          key={take.id}
          take={take}
          objective={take.proximateObjectiveId ? objMap[take.proximateObjectiveId] : undefined}
        />
      ))}

      {/* Add */}
      <button
        onClick={handleAddTake}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--bg-raised)] px-3 py-1.5 border border-[var(--rule)] transition-colors"
      >
        <PlusIcon className="w-4 h-4" />
        Add team take
      </button>

      {takes.length === 0 && (
        <p className="text-sm text-[var(--text-ter)] italic">
          No takes yet. Add a take for each team that has commitments in this strategy.
        </p>
      )}
    </div>
  );
}
